import { TractorAxlesFormName } from '@contracts/order/fields/TractorInformationFields';
import type { Timestamp } from '@src/common/time/Timestamp';
import { getTrailerField as getFormTrailerFields } from '@src/common/utils/form/ValueGetters';
import { AxleFieldsType } from '@src/permitField/AxleInformationFields';
import type { fields } from '@src/permitField/Fields';
import { getFieldsBySectionsInCategory } from '@src/permitField/Fields';
import {
  shouldDisplayTractorAxlesCount,
  TractorAxleCountField,
} from '@src/permitField/TractorInformationFields';
import {
  shouldDisplayTrailerAxlesCount,
  TrailerAxleCountField,
  TrailersFormName,
} from '@src/permitField/TrailerInformationFields';
import { EffectiveDateField } from '@src/permitField/TripInformationFields';

export type EntryValue =
  | string
  | number
  | boolean
  | object
  | undefined
  | Record<string, string>[];

export enum PermitStatus {
  NEW = 'NEW',
  WITH_JURISDICTION = 'WITH_JURISDICTION',
  ISSUED = 'ISSUED',
  ISSUED_ERROR = 'ISSUED_ERROR',
  DENIED = 'DENIED',
  CANCELLED = 'CANCELLED',
  HOLD = 'HOLD',
}

export const transformToPermitStatus = (value: string): PermitStatus => {
  if (!Object.values(PermitStatus).includes(value as PermitStatus)) {
    throw new Error(`Invalid PermitStatus: ${value}`);
  }
  return value as PermitStatus;
};

export interface Document {
  documentId: string;
  key: string;
  displayName?: string;
  sendToClient: boolean;
  showToClient: boolean;
}

export interface ConditionsSheet {
  id: string;
  filePath: string;
  sendToClient: boolean;
  showToClient: boolean;
}

export interface Assignee {
  id: string;
  name: string;
}

interface Attributes {
  id: string;
  permitTypeId: string;
  permitTypeOriginalId: string;
  documents: Document[];
  otherDocuments: Document[];
  statePermitDocument?: Document;
  conditionsSheets: ConditionsSheet[];
  fields: string[];
  entries: Record<string, EntryValue>;
  assignee?: Assignee;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  entriesUpdatedAt: Timestamp;
  status: PermitStatus;
  permitIndex: number;
  statePermitNumber?: string;
  effectiveDate: Timestamp;
  expirationDate?: Timestamp;
}

export class PermitApplication {
  public readonly id: string;
  public readonly permitTypeId: string;
  public readonly permitTypeOriginalId: string;
  public readonly statePermitDocument?: Document;
  public readonly documents: Document[];
  public readonly otherDocuments: Document[];
  public readonly conditionsSheets: ConditionsSheet[];
  public readonly fields: string[];
  public readonly entries: Record<string, EntryValue>;
  public readonly assignee?: Assignee;
  public readonly permitIndex: number;
  public readonly status: PermitStatus;
  public readonly statePermitNumber?: string;
  public readonly expirationDate?: Timestamp;
  public readonly createdAt: Timestamp;
  public readonly updatedAt: Timestamp;
  public readonly entriesUpdatedAt: Timestamp;
  public readonly effectiveDate: Timestamp;

  constructor(attributes: Attributes) {
    this.id = attributes.id;
    this.permitTypeId = attributes.permitTypeId;
    this.permitTypeOriginalId = attributes.permitTypeOriginalId;
    this.statePermitDocument = attributes.statePermitDocument;
    this.documents = attributes.documents;
    this.otherDocuments = attributes.otherDocuments;
    this.conditionsSheets = attributes.conditionsSheets;
    this.fields = attributes.fields;
    this.entries = attributes.entries;
    this.assignee = attributes.assignee;
    this.permitIndex = attributes.permitIndex;
    this.status = attributes.status;
    this.statePermitNumber = attributes.statePermitNumber;
    this.expirationDate = attributes.expirationDate;
    this.updatedAt = attributes.updatedAt;
    this.entriesUpdatedAt = attributes.entriesUpdatedAt;
    this.createdAt = attributes.createdAt;
    this.effectiveDate = attributes.effectiveDate;
  }

  getFieldValue(field: string): EntryValue | Record<string, EntryValue>[] {
    if (field === EffectiveDateField.fieldName) {
      return this.effectiveDate.format();
    }
    return this.entries[field];
  }

  getFieldsBySection(categoryName: keyof typeof fields) {
    const categoryFieldsBySection = getFieldsBySectionsInCategory(categoryName);
    const fieldsBySection = new Map<string, string[]>();

    categoryFieldsBySection.forEach((sectionFields, section) => {
      const fields = this.fields.filter((field) => {
        if (Array.isArray(sectionFields)) {
          return sectionFields.includes(field);
        }
        return sectionFields[section].includes(field);
      });
      if (fields.length > 0) {
        fieldsBySection.set(section, fields);
      }

      if (
        section === 'tractorInformation' &&
        !shouldDisplayTractorAxlesCount(this.fields)
      ) {
        const sectionFields = fieldsBySection.get(section);
        const sectionFieldsWithoutAxleCount = sectionFields?.filter(
          (field) => field !== TractorAxleCountField.fieldName,
        );

        if (sectionFieldsWithoutAxleCount) {
          fieldsBySection.set(section, sectionFieldsWithoutAxleCount);
        }
      }

      if (
        section === 'trailers' &&
        !shouldDisplayTrailerAxlesCount(this.fields)
      ) {
        const sectionFields = fieldsBySection.get(section);
        const sectionFieldsWithoutAxleCount = sectionFields?.filter(
          (field) => field !== TrailerAxleCountField.fieldName,
        );

        if (sectionFieldsWithoutAxleCount) {
          fieldsBySection.set(section, sectionFieldsWithoutAxleCount);
        }
      }
    });

    return fieldsBySection;
  }

  hasField(fieldName: string): boolean {
    return this.fields.includes(fieldName);
  }

  isReadOnly(): boolean {
    return (
      this.status === PermitStatus.ISSUED ||
      this.status === PermitStatus.ISSUED_ERROR ||
      this.status === PermitStatus.DENIED
    );
  }

  getEntries() {
    return this.entries;
  }

  isEffective(): boolean {
    return this.status !== PermitStatus.CANCELLED;
  }

  getTrailerField(fieldName: string, index: number): string | undefined {
    return getFormTrailerFields(fieldName, index, this.entries)?.toString();
  }

  isNew() {
    return this.status === PermitStatus.NEW;
  }

  hasValueForField(fieldName: string): boolean {
    if (!this.hasField(fieldName)) {
      return false;
    }

    if (AxleFieldsType.includes(fieldName as (typeof AxleFieldsType)[number])) {
      return this.hasValueForAxleField(fieldName);
    }

    const value = this.entries[fieldName];
    return value !== undefined && !isEntryValueEmpty(value);
  }

  private hasValueForAxleField(axleFieldName: string): boolean {
    const allAxles = this.getAllAxles();
    return allAxles.some((axle) => {
      const value = (axle as Record<string, unknown>)[axleFieldName];
      return hasNonEmptyValue(value);
    });
  }

  private getAllAxles(): Record<string, unknown>[] {
    const tractorAxles = this.getFieldValue(TractorAxlesFormName);
    const tractorAxlesList = extractAxlesFromValue(tractorAxles) ?? [];

    const trailers = this.getFieldValue(TrailersFormName);
    const trailerAxlesList = Array.isArray(trailers)
      ? trailers.flatMap((trailer) => extractAxlesFromValue(trailer) ?? [])
      : [];

    return [...tractorAxlesList, ...trailerAxlesList];
  }
}

const extractAxlesFromValue = (
  value: unknown,
): Record<string, unknown>[] | null => {
  if (!value || typeof value !== 'object' || !('axles' in value)) {
    return null;
  }

  const axles = (value as { axles: unknown }).axles;
  return Array.isArray(axles) ? axles : null;
};

const hasNonEmptyValue = (value: unknown): boolean => {
  return value !== undefined && value !== '' && value !== null;
};

export const isEntryValueEmpty = (entryValue: EntryValue): boolean => {
  if (!!entryValue && typeof entryValue === 'object') {
    return (
      Object.keys(entryValue).length === 0 ||
      Object.values(entryValue).every((value) => isEntryValueEmpty(value))
    );
  }

  return (
    entryValue === '' ||
    entryValue === 0 ||
    entryValue === false ||
    entryValue === undefined
  );
};
